/**
 * go2rtc WHEP /api/webrtc?src=... 재생 (관제 주경로).
 * 실패 시 onFallback → HLS (호출측이 무시하면 WebRTC 유지).
 * statsReport 있으면 getStats → brand-manager 관제 구간 보고.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { startWhepViewerStatsLoop } from '../../services/whepViewerStatsReporter';

export type Go2rtcWhepPlayerProps = {
  whepUrl: string;
  muted?: boolean;
  onFallback?: (reason: string) => void;
  onPlaying?: () => void;
  style?: React.CSSProperties;
  /** 통합관제: WHEP getStats 보고 (pool/cctv) */
  statsReport?: { poolId: number; cctvId: number } | null;
};

export type Go2rtcWhepPlayerHandle = {
  getVideoElement: () => HTMLVideoElement | null;
};

type StopFn = (opts?: { clearVideo?: boolean }) => void;

type PlayResult = {
  stop: StopFn;
  pc: RTCPeerConnection;
};

async function playWhep(
  video: HTMLVideoElement,
  whepUrl: string,
  onIceFailed: (reason: string) => void
): Promise<PlayResult> {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    bundlePolicy: 'max-bundle',
  });

  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  let iceGraceTimer: number | null = null;
  const clearIceGrace = () => {
    if (iceGraceTimer != null) {
      window.clearTimeout(iceGraceTimer);
    }
    iceGraceTimer = null;
  };

  pc.ontrack = (ev) => {
    if (ev.streams && ev.streams[0]) {
      video.srcObject = ev.streams[0];
    } else {
      const ms = new MediaStream([ev.track]);
      video.srcObject = ms;
    }
    void video.play().catch(() => undefined);
  };

  // disconnected 는 흔한 순간 유실 — tear-down 하면 관제가 뚝뚝 끊긴다.
  // failed 만 길게 유예 후 재연결.
  pc.oniceconnectionstatechange = () => {
    const st = pc.iceConnectionState;
    if (st === 'connected' || st === 'completed') {
      clearIceGrace();
      return;
    }
    if (st === 'failed') {
      if (iceGraceTimer != null) return;
      iceGraceTimer = window.setTimeout(() => {
        iceGraceTimer = null;
        onIceFailed(`ICE ${st}`);
      }, 12000);
    }
  };
  pc.onconnectionstatechange = () => {
    const st = pc.connectionState;
    if (st === 'connected') {
      clearIceGrace();
      return;
    }
    if (st === 'failed') {
      if (iceGraceTimer != null) return;
      iceGraceTimer = window.setTimeout(() => {
        iceGraceTimer = null;
        onIceFailed(`PC ${st}`);
      }, 12000);
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  await new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const t = window.setTimeout(resolve, 2500);
    pc.onicegatheringstatechange = () => {
      if (pc.iceGatheringState === 'complete') {
        window.clearTimeout(t);
        resolve();
      }
    };
  });

  const local = pc.localDescription;
  if (!local?.sdp) {
    clearIceGrace();
    pc.close();
    throw new Error('no local SDP');
  }

  const res = await fetch(whepUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: local.sdp,
    signal: AbortSignal.timeout(12000),
  });
  if (!res.ok) {
    clearIceGrace();
    pc.close();
    if (res.status === 404) {
      throw new Error(
        'WHEP HTTP 404 — 중앙 go2rtc에 스트림 없음. 서버에서 go2rtc·nginx /go2rtc/·WHIP 수신 확인'
      );
    }
    throw new Error(`WHEP HTTP ${res.status}`);
  }
  const answer = await res.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });

  return {
    stop: (opts) => {
      clearIceGrace();
      try {
        pc.close();
      } catch {
        /* ignore */
      }
      // 재연결 중 마지막 프레임 유지 (흑화면 깜빡임 완화)
      if (opts?.clearVideo !== false) {
        video.srcObject = null;
      }
    },
    pc,
  };
}

const Go2rtcWhepPlayer = forwardRef<Go2rtcWhepPlayerHandle, Go2rtcWhepPlayerProps>(
  function Go2rtcWhepPlayer(
    { whepUrl, muted = true, onFallback, onPlaying, style, statsReport = null },
    ref
  ) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const cleanupRef = useRef<null | StopFn>(null);
    const statsStopRef = useRef<null | (() => void)>(null);
    const onFallbackRef = useRef(onFallback);
    const onPlayingRef = useRef(onPlaying);
    const statsReportRef = useRef(statsReport);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    onFallbackRef.current = onFallback;
    onPlayingRef.current = onPlaying;
    statsReportRef.current = statsReport;

    useImperativeHandle(ref, () => ({
      getVideoElement: () => videoRef.current,
    }));

    useEffect(() => {
      const video = videoRef.current;
      const url = (whepUrl || '').trim();
      if (!video || !url) {
        setLoading(false);
        setError('WebRTC URL 없음');
        onFallbackRef.current?.('no url');
        return;
      }

      let cancelled = false;
      let played = false;
      let attempt = 0;
      const gate = { release: () => undefined as void };
      setLoading(true);
      setError(null);

      const stopStats = () => {
        if (statsStopRef.current) {
          statsStopRef.current();
          statsStopRef.current = null;
        }
      };

      const onVideoPlaying = () => {
        if (cancelled) return;
        played = true;
        attempt = 0;
        setLoading(false);
        setError(null);
        onPlayingRef.current?.();
      };
      video.addEventListener('playing', onVideoPlaying);

      const sleep = (ms: number) =>
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, ms);
        });

      (async () => {
        while (!cancelled) {
          attempt += 1;
          if (!played) setLoading(true);
          const lost = new Promise<void>((resolve) => {
            gate.release = resolve;
          });
          let stop: StopFn | null = null;
          try {
            const playedResult = await playWhep(video, url, () => gate.release());
            stop = playedResult.stop;
            if (cancelled) {
              stop();
              return;
            }
            cleanupRef.current = stop;
            stopStats();
            const sr = statsReportRef.current;
            if (sr && sr.poolId > 0 && sr.cctvId > 0) {
              statsStopRef.current = startWhepViewerStatsLoop(
                playedResult.pc,
                sr.poolId,
                sr.cctvId
              );
            }
            if (video.readyState >= 2) {
              played = true;
              attempt = 0;
              setLoading(false);
              setError(null);
              onPlayingRef.current?.();
            }
            const giveUp = window.setTimeout(() => {
              if (!played) gate.release();
            }, 20000);
            await lost;
            window.clearTimeout(giveUp);
            if (cancelled) return;
            stopStats();
            // 재생 이력이 있으면 마지막 프레임 유지한 채 재연결
            stop({ clearVideo: !played });
            cleanupRef.current = null;
          } catch (e) {
            stopStats();
            if (stop) stop({ clearVideo: !played });
            cleanupRef.current = null;
            if (cancelled) return;
            const msg = e instanceof Error ? e.message : String(e);
            if (!played && attempt >= 8) {
              setError(msg);
              setLoading(false);
              if (attempt === 8) onFallbackRef.current?.(msg);
            }
          }
          if (cancelled) return;
          const backoff = played
            ? 2000
            : Math.min(15000, 2000 + attempt * 500);
          await sleep(backoff);
        }
      })();

      return () => {
        cancelled = true;
        gate.release();
        video.removeEventListener('playing', onVideoPlaying);
        stopStats();
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
      };
      // statsReport 는 ref 로만 — 인라인 객체 deps 시 WHEP 재연결 폭주 방지
    }, [whepUrl]);

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000' }}>
        <video
          ref={videoRef}
          muted={muted}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            ...style,
          }}
        />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}
          >
            WebRTC: {error}
          </Typography>
        )}
      </Box>
    );
  }
);

export default Go2rtcWhepPlayer;
